import { useState } from "react";
import { api, ApiError, type CatalogueResponse } from "../lib/api.ts";

interface AddBookingFormProps {
  token: string;
  catalogue: CatalogueResponse;
  defaultDate: string;
  onCreated: (reference: string) => void | Promise<void>;
  onUnauthorised: () => void;
}

/**
 * A booking taken over the phone or at the door.
 *
 * No payment and no opening-hours check - the studio can fit someone in on a
 * closed day if it wants to. Clashes are still refused by the API.
 */
export function AddBookingForm({
  token,
  catalogue,
  defaultDate,
  onCreated,
  onUnauthorised,
}: AddBookingFormProps) {
  const services = catalogue.categories.flatMap(
    (category) => category.services,
  );
  const [serviceId, setServiceId] = useState(services[0]?.id ?? 0);
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState("10:00");
  const [customerName, setName] = useState("");
  const [customerPhone, setPhone] = useState("");
  const [customerEmail, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.admin.createBooking(token, {
        serviceId,
        date,
        time,
        customerName: customerName.trim(),
        ...(customerEmail.trim()
          ? { customerEmail: customerEmail.trim() }
          : {}),
        ...(customerPhone.trim()
          ? { customerPhone: customerPhone.trim() }
          : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      await onCreated(result.reference);
      setName("");
      setPhone("");
      setEmail("");
      setNotes("");
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The booking could not be added.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel admin-form" onSubmit={submit}>
      <h3 className="admin-form__title">Add a booking</h3>
      <p className="field-hint admin-form__hint">
        For phone and walk-in bookings. No deposit is taken and opening hours
        are not enforced, but an existing appointment cannot be double-booked.
      </p>

      <div className="admin-form__grid">
        <div className="field">
          <label htmlFor="add-service">Treatment</label>
          <select
            id="add-service"
            className="select"
            value={serviceId}
            onChange={(event) => setServiceId(Number(event.target.value))}
          >
            {catalogue.categories.map((category) => (
              <optgroup key={category.id} label={category.name}>
                {category.services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} ({service.durationMinutes} min)
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="add-date">Date</label>
          <input
            id="add-date"
            type="date"
            className="input"
            value={date}
            required
            onChange={(event) => setDate(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="add-time">Start time</label>
          <input
            id="add-time"
            type="time"
            className="input"
            value={time}
            step={900}
            required
            onChange={(event) => setTime(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="add-name">Client name</label>
          <input
            id="add-name"
            className="input"
            value={customerName}
            required
            minLength={2}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="add-phone">Phone</label>
          <input
            id="add-phone"
            type="tel"
            className="input"
            value={customerPhone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="add-email">Email (optional)</label>
          <input
            id="add-email"
            type="email"
            className="input"
            value={customerEmail}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="add-notes">Notes</label>
        <textarea
          id="add-notes"
          className="textarea"
          value={notes}
          maxLength={1000}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>

      {error && <p className="notice notice--error">{error}</p>}

      <button
        type="submit"
        className="btn"
        disabled={busy || !customerName.trim()}
      >
        {busy ? <span className="spinner" /> : "Add booking"}
      </button>
    </form>
  );
}
